import React, {useState, useContext} from 'react';
import DataTable from '@preflighttech/preflight-tables';
import { Pressable, Text, View } from 'react-native';
import { useMutation } from '@apollo/client';
import moment from 'moment';

import { BUYERS_WITH_COUNT, HIDE_BUYER } from './queries';
import SubNav from './SubNav';
import { Link } from 'app/src/utils/routing';
import Filter from 'app/src/elements/Filter';
import { Button } from 'app/src/elements/buttons';
import { ResetSettings } from 'app/src/elements/Pagination/ResetSettings';
import { filterButton } from 'app/src/elements/Pagination/FilterStyles';
import { abbreviations as states} from 'app/src/elements/states';
import Modal from '../../elements/Modal';

import EditBuyer from './edit';
import MergeBuyer from './Merge';

import {
  BuyersContainer
} from './elements';
import { LinkContainer, LinkSpace, MainContainer } from '../../elements';
import AuthContext from '../../contexts/AuthContext';

const inputStyle = {
  marginBottom: '10px'
}

const Buyers = () => {
  const { user } = useContext(AuthContext);
  const [refetch, setRefetch] = useState();
  const [filterOptions, setFilterOptions] = useState();
  const [pendingReviewCount, setPendingReviewCount] = useState();
  const [statusScope, setStatusScope] = useState('active');
  const [filters, setFilters] = useState({});
  const [clickedBuyer, setClickedBuyer] = useState();
  const [showEditBuyerModal, setShowEditBuyerModal] = useState(false);
  const [showMergeModal, setShowMergeModal] = useState();
  const queryVariables = {
    filterColumns: [],
    filterNames: [],
    scopes: [statusScope],
  };
  const [hideBuyer] = useMutation(
    HIDE_BUYER, { onCompleted: refetch }
  )

  const onHide = (buyer) => {
    if(window.confirm(`Hide ${buyer.name}?`)) {
      hideBuyer({ variables: { id: buyer.id }});
    }
  };

  const setFilter = (key, value) => {
    const currentFilters = {...filters};
    currentFilters[key] = value;
    setFilters(currentFilters);
  }

  Object.entries(filters).forEach(([key, value]) => {
    if (value) {
      queryVariables.filterColumns.push(key);
      queryVariables.filterNames.push(value);
    }
  });

  const columns = [
    {
      key: 'name',
      label: 'Buyer Name',
      sort: 'asc',
      content: ({ entry, value }) => (
        <Link to={`/buyers/${entry.id}`}>{value}</Link>
      )
    },
    {
      key: 'companyName',
      label: 'Company Name',
      content: ({ entry, value }) => (
        <Link to={`/buyers/${entry.id}`}>{value}</Link>
      )
    },
    {
      key: 'street',
      label: 'Address',
    },
    {
      key: 'city',
      label: 'City',
    },
    {
      key: 'state',
      label: 'State',
    },
    {
      key: 'postalCode',
      label: 'Zip',
      value: ({ value }) => value?.formatted
    },
    {
      key: 'phoneNumber',
      label: 'Phone',
      minViewportWidth: 1000,
      value: (entry) => entry.entry.phoneNumber?.formatted,
    },
    {
      key: 'email',
      label: 'Email',
      minViewportWidth: 1000,
      value: (entry) => entry.entry.email,
    },
    {
      key: 'createdAt',
      label: 'Registered On Web',
      value: ({ entry }) => entry.registeredAt,
      content: ({ value }) => value ? moment(value).format(' MM-DD-YY') : '',
    },
    {
      key: 'notes',
      label: 'Notes',
    },
    {
      key: 'functions',
      label: 'Functions',
      // width: '15%',
      // minViewportWidth: 1000,
      content: ({ entry }) => {
        return (
          <LinkContainer>
            <Link to={`/buyers/${entry.id}`}>
              {'pending_review' === statusScope ? 'Review' : 'View'}
            </Link>
            <LinkSpace/>
            <Pressable
              onPress={() => {
                setClickedBuyer(entry);
                setShowEditBuyerModal(true);
              }}
            >
              <Text>Edit</Text>
            </Pressable>
            <LinkSpace/>
            {user?.admin && (
              <>
                <Pressable onPress={() => setShowMergeModal(entry)}>
                  <Text>Merge</Text>
                </Pressable>
                <LinkSpace />
                <Pressable onPress={() => onHide(entry)}>
                  <Text>Hide</Text>
                </Pressable>
              </>
            )}
          </LinkContainer>
        )
      }
    }
  ]

  const buttons = (
    <View style={{display: 'flex', flexDirection: 'row'}}>
      {'pending_review' === statusScope ? (
        <Button
          style={filterButton}
          label={`Show approved buyers`}
          onPress={() => setStatusScope('active')}
        />
      ) : (
        <>
          {!!pendingReviewCount && (
            <Button
              style={filterButton}
              label={
                `Show ${pendingReviewCount} buyers pending review for bidding`
              }
              onPress={() => setStatusScope('pending_review')}
            />
          )}
        </>
      )}
      {'declined' === statusScope ? (
        <Button
          style={filterButton}
          label={`Show approved buyers`}
          onPress={() => setStatusScope('active')}
        />
      ) : (
        <Button
          style={filterButton}
          label={`Show declined buyers`}
          onPress={() => setStatusScope('declined')}
        />
      )}
      <Filter
        clearFilters={() => {
          setFilters({})
          setShowModal(false)
        }}
        filters={filters}
        setFilters={setFilters}
      >
        <View style={{padding: '20px', justifyContent: 'space-between'}}>
          <Text>Buyer Name:</Text>
          <input
            type={'text'}
            name="name"
            onChange={e => setFilter('name', e.target.value)}
            value={filters.name || ''}
            style={inputStyle}
          />
          <Text>City:</Text>
          <input
            type={'text'}
            name="City"
            onChange={e => setFilter('city', e.target.value)}
            value={filters.city || ''}
            style={inputStyle}
          />
          <Text>State:</Text>
          <select
            type={'select'}
            name="state"
            onChange={e => setFilter('state', e.target.value)}
            value={filters.state || ''}
          >
            <option key={`state-all`}>
              {'All'}
            </option>
            {states.map(state =>
              <option key={`state-${state}`}>
                {state}
              </option>
            )}
          </select>
        </View>
      </Filter>
      <ResetSettings />
    </View>
  );

  return (
    <MainContainer>
      <SubNav refetch={refetch} />
      <BuyersContainer>
        <DataTable.Gql
          setRefetch={setRefetch}
          query={BUYERS_WITH_COUNT}
          queryName='buyersWithCount'
          columns={columns}
          buttons={buttons}
          lengthMenu={ [10, 25, 50, 'All'] }
          dataUpdated={data => {
            setPendingReviewCount(data?.buyersWithCount.pendingReviewCount);
            setFilterOptions(data?.buyersWithCount);
          }}
          htmlTable
          queryVariables={queryVariables}
        />
        {showEditBuyerModal &&
          <Modal
            refetch={refetch}
            showModal={showEditBuyerModal}
            setShowModal={setShowEditBuyerModal}
            modalType={'Edit Buyer'}
            objectName={clickedBuyer.name}
          >
            <EditBuyer
              setShowModal={setShowEditBuyerModal}
              refetch={refetch}
              buyer={clickedBuyer}
            />
          </Modal>
        }
        {showMergeModal &&
          <Modal
            refetch={refetch}
            showModal={showMergeModal}
            setShowModal={setShowMergeModal}
            modalType="Merge Buyer"
            objectName={showMergeModal.name}
          >
            <MergeBuyer
              setShowModal={setShowMergeModal}
              refetch={refetch}
              buyer={showMergeModal}
            />
          </Modal>
        }
      </BuyersContainer>
    </MainContainer>
  )
}

export default Buyers;
